import React, { useEffect, useRef, useState } from 'react';
import { API } from '../api';
import { getNotificationStatusLabel, normalizeNotificationStatusPayload } from '../utils/notificationStatus';
import './NotificationPanel.css';

const initialProgress = {
  status: null,
  totalRecipients: 0,
  sentCount: 0,
  failedCount: 0,
  processedCount: 0,
  remainingCount: 0,
  startedAt: null,
  finishedAt: null,
  lastError: null
};

const NotificationPanel = ({ onCampaignCreated }) => {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [parseMode, setParseMode] = useState('Markdown');
  const [mediaType, setMediaType] = useState('text');
  const [mediaUrl, setMediaUrl] = useState('');
  const [buttons, setButtons] = useState([{ text: '', url: '' }]);
  const [campaignId, setCampaignId] = useState(null);
  const [progress, setProgress] = useState(initialProgress);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const pollTimer = useRef(null);

  useEffect(() => {
    return () => {
      if (pollTimer.current) {
        clearTimeout(pollTimer.current);
      }
    };
  }, []);

  const updateButton = (index, field, value) => {
    setButtons((prev) => prev.map((button, idx) => idx === index ? { ...button, [field]: value } : button));
  };

  const addButton = () => {
    if (buttons.length >= 3) return;
    setButtons((prev) => [...prev, { text: '', url: '' }]);
  };

  const removeButton = (index) => {
    setButtons((prev) => prev.filter((_, idx) => idx !== index));
  };

  const buildPayload = () => ({
    title: title.trim() || undefined,
    message: message.trim(),
    parseMode,
    mediaType: mediaType === 'text' ? undefined : mediaType,
    mediaUrl: mediaType === 'text' ? undefined : mediaUrl.trim() || undefined,
    buttons: buttons
      .filter((button) => button.text.trim() && button.url.trim())
      .map((button) => ({ text: button.text.trim(), url: button.url.trim() })),
  });

  const validatePayload = (payload) => {
    if (!payload.message) {
      return 'Введите текст сообщения.';
    }

    if (payload.mediaType && !payload.mediaUrl) {
      return 'Укажите mediaUrl для фото или видео.';
    }

    const hasIncompleteButton = buttons.some((button) => {
      const hasText = button.text.trim().length > 0;
      const hasUrl = button.url.trim().length > 0;
      return hasText !== hasUrl;
    });

    if (hasIncompleteButton) {
      return 'Для каждой кнопки нужно заполнить и текст, и URL.';
    }

    return '';
  };

  const handleApiError = (errorText) => {
    setError(errorText || 'Не удалось выполнить запрос.');
  };

  const handlePollStatus = async (id) => {
    if (!id) return;
    const result = await API.notifications.status(id);
    if (!result.success) {
      handleApiError(result.error || 'Не удалось получить статус рассылки.');
      return;
    }

    const campaign = normalizeNotificationStatusPayload(result.data);
    setProgress(campaign);

    if (campaign.status === 'pending' || campaign.status === 'in_progress') {
      pollTimer.current = setTimeout(() => handlePollStatus(id), 2000);
    }
  };

  const handleTestSend = async () => {
    setError('');
    setLoading(true);
    try {
      const payload = buildPayload();
      const validationError = validatePayload(payload);
      if (validationError) {
        setError(validationError);
        return;
      }
      const result = await API.notifications.testSend(payload);
      if (!result.success) {
        handleApiError(result.error);
        return;
      }
      setCampaignId(null);
      setProgress({ ...initialProgress, status: 'test_sent' });
    } catch (e) {
      handleApiError(e?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStart = async () => {
    setError('');
    setLoading(true);
    try {
      const payload = buildPayload();
      const validationError = validatePayload(payload);
      if (validationError) {
        setError(validationError);
        return;
      }
      const result = await API.notifications.start(payload);
      if (!result.success) {
        handleApiError(result.error);
        return;
      }
      const id = result.data?.id;
      setCampaignId(id);
      setProgress({
        ...initialProgress,
        status: result.data?.status || 'pending',
        totalRecipients: result.data?.totalRecipients || 0,
        sentCount: result.data?.sentCount || 0,
        failedCount: result.data?.failedCount || 0,
        remainingCount: result.data?.totalRecipients || 0
      });
      if (onCampaignCreated) {
        onCampaignCreated(result.data);
      }
      handlePollStatus(id);
    } catch (e) {
      handleApiError(e?.message);
    } finally {
      setLoading(false);
    }
  };

  const progressPercentage = progress.totalRecipients > 0
    ? Math.round(((progress.sentCount + progress.failedCount) / progress.totalRecipients) * 100)
    : 0;

  return (
    <div className="notification-panel">
      <div className="notification-panel__header">
        <div>
          <h2>Массовая рассылка Telegram</h2>
          <p>Отправляйте текст, фото или видео всем пользователям бота. Перед запуском можно отправить тестовую копию на жестко заданный Telegram ID.</p>
        </div>
      </div>

      <div className="notification-panel__grid">
        <label className="notification-panel__field">
          <span>Название кампании (для истории)</span>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Например, Новое обновление" />
        </label>

        <label className="notification-panel__field full-width">
          <span>Текст сообщения</span>
          <textarea
            rows={5}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Введите сообщение для пользователей..."
          />
        </label>

        <div className="notification-panel__field">
          <span>Формат Telegram</span>
          <select value={parseMode} onChange={(e) => setParseMode(e.target.value)}>
            <option value="Markdown">Markdown</option>
            <option value="MarkdownV2">MarkdownV2</option>
            <option value="HTML">HTML</option>
          </select>
        </div>

        <div className="notification-panel__field">
          <span>Тип контента</span>
          <select value={mediaType} onChange={(e) => setMediaType(e.target.value)}>
            <option value="text">Только текст</option>
            <option value="photo">Фото</option>
            <option value="video">Видео</option>
          </select>
        </div>

        {mediaType !== 'text' && (
          <label className="notification-panel__field full-width">
            <span>URL медиа</span>
            <input value={mediaUrl} onChange={(e) => setMediaUrl(e.target.value)} placeholder="https://example.com/image.jpg" />
          </label>
        )}

        <div className="notification-panel__buttons-section full-width">
          <div className="notification-panel__buttons-header">
            <span>Кнопки (Inline)</span>
            <button type="button" className="notification-panel__secondary-button" onClick={addButton} disabled={buttons.length >= 3}>
              Добавить кнопку
            </button>
          </div>

          {buttons.map((button, index) => (
            <div className="notification-panel__button-row" key={index}>
              <input
                value={button.text}
                onChange={(e) => updateButton(index, 'text', e.target.value)}
                placeholder="Текст кнопки"
              />
              <input
                value={button.url}
                onChange={(e) => updateButton(index, 'url', e.target.value)}
                placeholder="URL кнопки"
              />
              <button type="button" className="notification-panel__remove-button" onClick={() => removeButton(index)}>
                Удалить
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="notification-panel__footer">
        <div className="notification-panel__actions">
          <button type="button" className="notification-panel__button" onClick={handleTestSend} disabled={loading || !message.trim()}>
            Отправить тест
          </button>
          <button type="button" className="notification-panel__button notification-panel__button--primary" onClick={handleStart} disabled={loading || !message.trim()}>
            Запустить рассылку
          </button>
        </div>

      <div className="notification-panel__meta">
          <div>Тест уходит на `NOTIFICATIONS_TEST_TELEGRAM_ID` или `OWNER_TELEGRAM_ID`.</div>
          {campaignId && <div>Кампания #{campaignId}</div>}
        </div>
      </div>

      <div className="notification-panel__status">
        <div className="notification-panel__status-row">
          <strong>Статус:</strong>
          <span className={`notifications-history__status notifications-history__status--${progress.status || 'idle'}`}>
            {getNotificationStatusLabel(progress.status)}
          </span>
        </div>
        {progress.totalRecipients > 0 && (
          <>
            <div className="notification-panel__status-row">
              <strong>Получателей:</strong> {progress.totalRecipients}
            </div>
            <div className="notification-panel__status-row">
              <strong>Отправлено:</strong> {progress.sentCount}
            </div>
            <div className="notification-panel__status-row">
              <strong>Ошибок:</strong> {progress.failedCount}
            </div>
            <div className="notification-panel__status-row">
              <strong>Обработано:</strong> {progress.processedCount}
            </div>
            <div className="notification-panel__status-row">
              <strong>Осталось:</strong> {progress.remainingCount}
            </div>
            <div className="notification-panel__progress-bar">
              <div className="notification-panel__progress-fill" style={{ width: `${progressPercentage}%` }} />
            </div>
          </>
        )}
        {progress.lastError && <div className="notification-panel__error">Последняя ошибка: {progress.lastError}</div>}
        {error && <div className="notification-panel__error">{error}</div>}
      </div>
    </div>
  );
};

export default NotificationPanel;
